import {
	type Country,
	CountrySchema,
	DateOfBirthSchema,
	type FetchRegistrationCountryCanRegisterByCountryData,
	isCustomApiError,
	type PostRegistrationUserValidationEmailData,
	type PostRegistrationUserValidationUsernameData,
	type PostRegistrationUserValidationUsernamePayload,
	type PostUserValidationEmailV3Payload,
	safeRequest,
	useCountries,
	useIntl,
	useSettings
} from '@capsicum/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtom, useAtomValue } from 'jotai';
import DebouncePromise from 'p-debounce';
import { useCallback, useEffect, useMemo } from 'react';
import { type FieldPath, useForm, type UseFormReturn } from 'react-hook-form';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { showEmailInputFormAtom } from '../atoms/atoms';
import { fetchCountryCanRegister, postValidationEmail, postValidationUsername } from '../data-access/api/register';
import { usernameRegistrationStepAtom } from '../data-access/atoms/usernameRegistrationStep.atoms';
import { type EmailFormattedMedium } from '../models/emailFormattedMedium';
import { type WelcomeVariant } from '../models/welcome';
import { type WelcomeFormattedMedium } from '../models/welcomeFormattedMedium';

import { useUserPrefilledData } from './useUserPrefilledData';

const zEmailSchema = z.string().email();

export const useFormValidations = () => {
	const { $t } = useIntl();
	const emailValidation = useEmailValidation();
	const countryCanRegisterValidation = useCountryCanRegisterValidation();

	const emailValidationDebounced = useMemo(() => DebouncePromise(emailValidation, 750), [emailValidation]);

	const countryCanRegisterValidationDebounced = useMemo(
		() => DebouncePromise(countryCanRegisterValidation, 750),
		[countryCanRegisterValidation]
	);

	const zMarketingConsent = useMemo(() => z.boolean().optional(), []);
	const zIsAbove18 = useMemo(() => z.boolean().refine(val => val), []);
	const zTermsAndConditions = useMemo(() => z.boolean().refine(val => val), []);
	const zDateOfBirth = useMemo(() => DateOfBirthSchema({ $t, checkAgeOfMajority: true }), [$t]);

	const zUsername = useMemo(
		() =>
			z
				.string()
				.min(3, $t('auth.usernameHelper'))
				.and(z.string().max(15, $t('auth.usernameHelper'))),
		[$t]
	);

	const zEmail = useMemo(
		() =>
			z.string().superRefine((val, ctx) => {
				// While we can validate an email using 'zEmailSchema', we encountered an issue when attempting to validate
				// lengthy strings lacking the '@' sign in React Native. For instance, "evripides.kardamilaschiliz.com" (notice
				// the absence of the '@' sign) triggers a RangeError, specifically: "Maximum regex stack depth reached, js
				// engine: hermes". This is why we are checking for the '@' sign before validating the string using Regex.
				if (!val.includes('@') || !zEmailSchema.safeParse(val).success) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						fatal: true,
						message: $t('auth.invalidEmail')
					});
				}

				return z.NEVER;
			}),
		[$t]
	);

	const zRegisterEmail = useMemo(
		() =>
			zEmail.superRefine(async (val, ctx) => {
				const { error } = await emailValidationDebounced({ email: val });

				error?.message &&
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: error.message
					});

				return z.NEVER;
			}),
		[zEmail, emailValidationDebounced]
	);

	const zLoginEmail = useMemo(
		() =>
			zEmail.superRefine(async (val, ctx) => {
				const { error } = await emailValidationDebounced({ email: val }, { checkForEmailTaken: false });

				error?.message &&
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: error.message
					});

				return z.NEVER;
			}),
		[zEmail, emailValidationDebounced]
	);

	const zCountry = useMemo(
		() =>
			CountrySchema.superRefine(async (val, ctx) => {
				const response = await countryCanRegisterValidationDebounced(val.id);

				if (response) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: response.message
					});
				}

				if (val.iso3 === 'USA') {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: $t('auth.registration.countryStep.blockedCountry')
					});
				}

				return z.NEVER;
			}),
		[$t, countryCanRegisterValidationDebounced]
	);

	return {
		zCountry,
		zDateOfBirth,
		zEmail,
		zIsAbove18,
		zLoginEmail,
		zMarketingConsent,
		zRegisterEmail,
		zTermsAndConditions,
		zUsername
	};
};

export const useEmailValidation = () => {
	const { $t } = useIntl();

	return useCallback(
		async (
			validationParams: PostUserValidationEmailV3Payload,
			options: {
				checkForEmailTaken: boolean;
			} = {
				checkForEmailTaken: true
			}
		): Promise<{
			error?: {
				field: FieldPath<EmailLoginFormType>;
				message?: string;
				type: string;
			};
			formattedValues?: EmailFormattedMedium;
		}> => {
			const { data, error, success } = await safeRequest(() => postValidationEmail(validationParams));

			if (success) {
				const error = match<
					Exclude<PostRegistrationUserValidationEmailData['data'], undefined>,
					| {
							field: FieldPath<EmailLoginFormType>;
							message?: string;
							type: string;
					  }
					| undefined
				>(data)
					.with({ isValid: false }, () => ({
						field: 'email',
						message: $t('auth.invalidEmail'),
						type: 'api'
					}))
					.with(
						{ isAvailable: false },
						() => options.checkForEmailTaken,
						() => ({
							field: 'email',
							message: $t('auth.emailTaken'),
							type: 'api'
						})
					)
					.otherwise(() => undefined);

				return {
					error,
					formattedValues: {
						email: validationParams.email!,
						isAvailable: data.isAvailable,
						isValid: data.isValid
					}
				};
			}

			const errorMessage = isCustomApiError(error) ? error.errors?.[0]?.title : $t('global.error');

			return {
				error: {
					field: 'email',
					/**
					 * @todo - Create a util to properly extract the errors
					 */
					message: errorMessage,
					type: 'api'
				}
			};
		},
		[$t]
	);
};

export const useSmsLoginSchema = () => {
	const { $t } = useIntl();

	return useMemo(
		() =>
			z.object({
				country: CountrySchema.refine(x => x.iso2 !== 'US', $t('auth.blockedCountry')),
				mobileNumber: z
					.string()
					.min(4, $t('auth.mobileNumberTooShort'))
					.regex(/^[0-9]+$/, $t('auth.invalidMobileNumber'))
			}),
		[$t]
	);
};

const useEmailLoginSchema = () => {
	const { $t } = useIntl();

	return useMemo(
		() =>
			z.object({
				email: z.string().email($t('auth.invalidEmail'))
			}),
		[$t]
	);
};

export const useEmailSMSLoginSchema = ({ variant }: { variant: WelcomeVariant }) => {
	const { $t } = useIntl();
	const { zLoginEmail, zRegisterEmail } = useFormValidations();
	const showEmailInput = useAtomValue(showEmailInputFormAtom);

	return useMemo(
		() =>
			z.object({
				country: CountrySchema.refine(x => x.iso2 !== 'US', $t('auth.blockedCountry')),
				id: showEmailInput
					? variant === 'login'
						? zLoginEmail
						: zRegisterEmail
					: z
							.string()
							.min(4, $t('auth.mobileNumberTooShort'))
							.regex(/^[0-9]+$/, $t('auth.invalidMobileNumber'))
			}),
		[$t, variant, zRegisterEmail, zLoginEmail, showEmailInput]
	);
};

export const useSmsLoginForm = ({ preventPrefillData } = { preventPrefillData: false }) => {
	const { prefilledCountry, prefilledPhoneNumber } = useUserPrefilledData();

	const loginForm = useForm<SmsLoginFormType>({
		defaultValues: useMemo(
			() => ({
				country: prefilledCountry,
				mobileNumber: prefilledPhoneNumber
			}),
			[prefilledCountry, prefilledPhoneNumber]
		),
		mode: 'onSubmit',
		resolver: zodResolver(useSmsLoginSchema())
	});

	// Resetting the country when we get users' country
	useEffect(() => {
		if (prefilledCountry && !preventPrefillData) {
			loginForm.reset({
				country: prefilledCountry,
				mobileNumber: prefilledPhoneNumber
			});
		}
	}, [loginForm, prefilledPhoneNumber, prefilledCountry]);

	return loginForm;
};

export const useEmailSMSLoginForm = (
	{ formattedValues, variant }: { formattedValues?: WelcomeFormattedMedium; variant: WelcomeVariant } = {
		variant: 'login'
	}
) => {
	const { prefilledCountry, prefilledPhoneNumber } = useUserPrefilledData();
	const { getCountryBy } = useCountries();
	const showEmailInput = useAtomValue(showEmailInputFormAtom);

	const { country, id } = useMemo<{ country?: Country; id?: string }>(() => {
		if (!formattedValues) return {};
		if (formattedValues.type === 'email' && showEmailInput) {
			return { id: formattedValues.medium.email };
		}
		if (formattedValues.type === 'phone-number' && !showEmailInput) {
			const country = getCountryBy(formattedValues.medium.countryCode, 'iso2');

			return {
				country,
				id: formattedValues.medium.mobileNumber.substring(country?.internationalCallingCode.length ?? 0)
			};
		}

		return {};
	}, [formattedValues, getCountryBy, showEmailInput]);

	const loginForm = useForm<EmailSMSLoginFormType>({
		defaultValues: {
			country: country ?? prefilledCountry,
			id: id ?? prefilledPhoneNumber
		},
		mode: 'onSubmit',
		resolver: zodResolver(useEmailSMSLoginSchema({ variant }))
	});

	// Resetting the country when we get users' country
	useEffect(() => {
		if (prefilledCountry && !formattedValues)
			loginForm.reset({ country: prefilledCountry, id: prefilledPhoneNumber });
	}, [loginForm, prefilledPhoneNumber, prefilledCountry, formattedValues]);

	return loginForm;
};

export const useEmailLoginForm = () =>
	useForm<EmailLoginFormType>({
		defaultValues: useMemo(
			() => ({
				email: ''
			}),
			[]
		),
		mode: 'onSubmit',
		resolver: zodResolver(useEmailLoginSchema())
	});

export const useAttemptSchema = () => {
	const { $t } = useIntl();

	return useMemo(() => z.number().min(0).max(3, $t('auth.tooManyAttempts')), [$t]);
};

export const useAttemptErrors = (attempt: number) => {
	const attemptSchema = useAttemptSchema();

	return useMemo(() => attemptSchema.safeParse(attempt), [attempt, attemptSchema]);
};

export type SmsLoginFormType = z.infer<ReturnType<typeof useSmsLoginSchema>>;
export type EmailLoginFormType = z.infer<ReturnType<typeof useEmailLoginSchema>>;
export type EmailSMSLoginFormType = z.infer<ReturnType<typeof useEmailSMSLoginSchema>>;
export type EmailSMSLoginFormSubmit = Parameters<UseFormReturn<EmailSMSLoginFormType>['handleSubmit']>[0];

const useUsernameSchema = () => {
	const {
		settings: { isUsernameSuggestionsEnabled }
	} = useSettings();

	const { $t } = useIntl();
	const { zUsername } = useFormValidations();
	const usernameValidation = useUsernameValidation();
	const [getUsernameRegistrationStepAtom, setUsernameRegistrationStepAtom] = useAtom(usernameRegistrationStepAtom);
	const usernameValidationDebounced = useMemo(() => DebouncePromise(usernameValidation, 750), [usernameValidation]);

	return useMemo(
		() =>
			zUsername.superRefine(async (val, ctx) => {
				const response = await usernameValidationDebounced({ username: val });

				if (!response?.suggestions) {
					setUsernameRegistrationStepAtom({ suggestions: [] });
				} else {
					if (!isUsernameSuggestionsEnabled) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: $t('auth.registration.usernameStep.usernameAlreadyTaken')
						});
					} else if (getUsernameRegistrationStepAtom.suggestions.length === 0) {
						setUsernameRegistrationStepAtom({
							suggestions:
								response?.suggestions && response.suggestions.length > 0 ? response.suggestions : []
						});

						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: $t('auth.registration.usernameStep.usernameAlreadyTakenWithSuggestions')
						});
					}
				}

				response?.message &&
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: response.message
					});

				return z.NEVER;
			}),
		[
			$t,
			zUsername,
			usernameValidationDebounced,
			isUsernameSuggestionsEnabled,
			getUsernameRegistrationStepAtom,
			setUsernameRegistrationStepAtom
		]
	);
};

// TODO: Add country state validation once US states are available
export const useUserRegisterSchema = () => {
	const usernameSchema = useUsernameSchema();
	const { zCountry, zIsAbove18, zMarketingConsent, zRegisterEmail, zTermsAndConditions } = useFormValidations();

	return useMemo(
		() =>
			z.object({
				country: zCountry,
				email: zRegisterEmail,
				isAbove18: zIsAbove18,
				// countryState: CountryStateSchema.optional(),
				marketingConsent: zMarketingConsent,
				termsAndConditions: zTermsAndConditions,
				username: usernameSchema.optional()
			}),
		[usernameSchema, zRegisterEmail, zCountry, zMarketingConsent, zTermsAndConditions, zIsAbove18]
	);
};

export type RegisterSchemaType = z.infer<ReturnType<typeof useUserRegisterSchema>>;

export const useCountryCanRegisterValidation = () => {
	const { $t } = useIntl();

	return useCallback(
		async (country: string): Promise<{ field: string; message?: string; type: string } | undefined> => {
			const { data, error, success } = await safeRequest(() => fetchCountryCanRegister(country));

			if (success) {
				const err = match<
					Exclude<FetchRegistrationCountryCanRegisterByCountryData['data'], undefined>,
					{ field: FieldPath<RegisterSchemaType>; message?: string; type: string } | undefined
				>(data)
					.with({ canRegister: false }, () => ({
						field: 'country',
						message: $t('auth.registration.countryStep.blockedCountry'),
						type: 'api'
					}))

					.otherwise(() => undefined);

				return err;
			}

			const errorMessage = isCustomApiError(error) ? error.errors?.[0]?.title : $t('global.error');

			return {
				field: 'country',
				/**
				 * @todo - Create a util to properly extract the errors
				 */
				message: errorMessage,
				type: 'api'
			};
		},
		[$t]
	);
};

export const useUsernameValidation = () => {
	const { $t } = useIntl();
	const {
		settings: { isUsernameStepRegistrationEnabled }
	} = useSettings();

	return useCallback(
		async (
			validationParams: PostRegistrationUserValidationUsernamePayload
		): Promise<{ field: string; message?: string; suggestions?: Array<string>; type: string } | undefined> => {
			if (!isUsernameStepRegistrationEnabled) {
				return;
			}

			const { data, error, success } = await safeRequest(() => postValidationUsername(validationParams));

			if (success) {
				const err = match<
					Exclude<PostRegistrationUserValidationUsernameData['data'], undefined>,
					{ field: FieldPath<RegisterSchemaType>; message?: string; type: string } | undefined
				>(data)
					.with({ isValid: false }, () => ({
						field: 'username',
						message: $t('auth.usernameInvalidError'),
						type: 'api'
					}))
					.with({ isAvailable: false }, () => ({
						field: 'username',
						message: $t('auth.usernameNotAvailableError'),
						suggestions: data.suggestions,
						type: 'apai'
					}))

					.otherwise(() => undefined);

				return err;
			}

			const errorMessage = isCustomApiError(error) ? error.errors?.[0]?.title : $t('global.error');

			return {
				field: 'username',
				/**
				 * @todo - Create a util to properly extract the errors
				 */
				message: errorMessage,
				type: 'api'
			};
		},
		[$t]
	);
};
